import { message, notification } from "antd";
import { DATE_FORMAT_DD_MM_YYYY, DATE_FORMAT_YYYY_MM_DD } from "constants/DateConstant";
import { showInfiniteNotification } from "functions/notifications";
import moment from "moment";
import { Trans } from "react-i18next";
import i18n from "translations/i18n";
import { FIRESTORE_ORDERS_TABLE } from "../../constants/FirestoreConstant";
import { ORDER_STATUS_SHIPPED } from "../../constants/OrderConstant";
import { DELIVERY_DURATION } from "../../constants/DeliveryConstants";

/**
 * Send all selected orders to OptimoRoute application through an API call.
 *
 * Writes `optimoRouteExportedAt` (unix seconds, same shape as `createdAt`) next to the SHIPPED
 * status. That field is this export's equivalent of Trunkrs' `trunkrsNumber` and SooCool's
 * `soocool.orderId`: without it OptimoRoute left nothing on the order at all, so once regional
 * orders became eligible for SooCool the same regional order could be handed to BOTH - two
 * pickups, two sets of labels, double billing. `getCarrierBooking` in the orders screen reads it.
 *
 * Note this writes to Firestore directly rather than through `useOrders.updateOrder`, because it
 * is handed a raw firestore instance - that is pre-existing and the reason the caller has to
 * `refreshOrders()` afterwards to see the marker.
 *
 * Outside production nothing is sent (see {@link isOptimoRouteLive}): the orders come back as
 * `simulated`, nothing is written on them, and staff see which orders would have gone.
 *
 * @param orders The orders that should be sent to the OptimoRoute
 * @param firestore
 * @param options.live Whether to really send; defaults to {@link isOptimoRouteLive}
 * @returns {Promise<{exported: Object[], failed: Object[], simulated: Object[]}>} the orders that
 *          were actually accepted by OptimoRoute, the ones that were not, and - outside production -
 *          the ones that would have been sent. Never rejects: a partial failure must still tell the
 *          caller which orders DID go out, because those - and only those - are the ones that get a
 *          Trustpilot invitation.
 */
export const createDeliveryOrder = async (orders, firestore, { live = isOptimoRouteLive() } = {}) => {
  if (!live) {
    showOptimoSimulation(orders || []);
    return { exported: [], failed: [], simulated: orders || [] };
  }

  const exported = [];
  const failed = [];
  let errorMessages = "";

  const results = await Promise.all(
    (orders || []).map((order) =>
      sendToOptimoRoute(order)
        .then(() => ({ order, errorMessage: null }))
        .catch((errorMessage) => ({ order, errorMessage: errorMessage || `${order.orderId}: onbekende fout\n\n` })),
    ),
  );

  for (const { order, errorMessage } of results) {
    if (errorMessage) {
      failed.push(order);
      errorMessages += errorMessage;
      continue;
    }

    try {
      // Awaited, unlike before: the caller refreshes the orders straight after this resolves, and
      // an un-awaited write meant the refresh could run before the marker existed.
      await firestore.collection(FIRESTORE_ORDERS_TABLE).doc(order.id).update({
        orderStatus: ORDER_STATUS_SHIPPED,
        optimoRouteExportedAt: moment().unix(),
      });
      exported.push(order);
    } catch (err) {
      // The route exists at OptimoRoute but we could not record it. Report it rather than
      // counting it as a clean export - the marker is what stops a second carrier booking.
      failed.push(order);
      errorMessages += `${order.orderId}: verstuurd naar OptimoRoute, maar niet vastgelegd op de bestelling (${err})\n\n`;
    }
  }

  showOptimoResults(exported.length, failed.length, errorMessages);

  return { exported, failed, simulated: [] };
};

/**
 * Whether an export really goes to OptimoRoute. Only in production (HVC-83): there is one OptimoRoute
 * account, the one the vans plan their day in, and every environment file carries its key. A test
 * export from a local or dev CMS would put real stops in tomorrow's route - with order numbers from the
 * dev project that may even collide with real ones.
 */
export const isOptimoRouteLive = (environment = process.env.REACT_APP_ENVIRONMENT) => environment === "production";

/** Outside production: say which orders would have gone, and that nothing was sent. */
const showOptimoSimulation = (orders) => {
  const lines = orders.map((order) => `${order.orderId} (${order.delivery?.date || "?"})`);
  notification.info({
    message: i18n.t("optimoroute_export.simulated_title", { count: orders.length }),
    description: (
      <div>
        <div>{i18n.t("optimoroute_export.simulated_description")}</div>
        <div style={{ marginTop: 8 }}>{lines.join(", ")}</div>
      </div>
    ),
    duration: 0,
  });
};

/**
 * Send order to OptimoRoute, add errorMessage to promise result to show to user if anything goes wrong
 * @param order
 */
const sendToOptimoRoute = (order) => {
  return new Promise((resolve, reject) => {
    let delivery = order.customer.address.delivery;
    const { street, houseNumber, addition, zipCode, city } = delivery;
    let deliveryAddress = street + " " + houseNumber + (!!addition ? ` ${addition}` : "") + ", " + zipCode + " " + city;
    fetch(process.env.REACT_APP_OPTIMO_ROUTE_CREATE_ORDER + process.env.REACT_APP_OPTIMO_ROUTE_API_KEY, {
      method: "POST",
      body: JSON.stringify({
        date: moment(order.delivery.date, DATE_FORMAT_DD_MM_YYYY).format(DATE_FORMAT_YYYY_MM_DD),
        location: {
          address: deliveryAddress,
        },
        orderNo: order.orderId,
        notes: !!order.orderNotes ? order.orderNotes : "",
        operation: "CREATE",
        type: "D",
        email: order.customer.email,
        duration: DELIVERY_DURATION,
        notificationPreference: "email",
      }),
    })
      .then((response) => response.json())
      .then((data) => {
        if (!data.success) {
          let errorMessage = order.orderId + ": " + data.message + "\n\n";
          reject(errorMessage);
        } else resolve();
      })
      .catch((err) => reject(err));
  });
};

/**
 * Show amount of orders that succesfully got sent to OptimoRoute and show error messages to user, if any.
 * @param counter Counter of succeeded orders sent to OptimoRoute
 * @param errorCounter Counter of orders that couldn't get sent to OptimoRoute
 * @param errorMessages All error messages in one string
 */
const showOptimoResults = (counter, errorCounter, errorMessages) => {
  if (counter > 0) {
    message.success(
      <Trans
        i18nKey="notifications.optimoroute_uploaded"
        values={{
          count: counter,
        }}
      />,
    );
  }
  if (errorCounter > 0) {
    showInfiniteNotification(i18n.t("notifications.fix_following_errors"), errorMessages);
  }
};
